import { getEscorts } from '@/data/mockData';
import { supabase } from '@/integrations/supabase/client';
import { EscortProfile } from '@/types';

// Function to sanitize object for Supabase (remove undefined values)
const sanitizeObject = (obj: Record<string, any>) => {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value !== undefined) {
      acc[key] = value;
    }
    return acc;
  }, {} as Record<string, any>);
};

interface ImportResult {
  success: boolean;
  count: number;
  error?: string;
  results?: { 
    status: string; 
    profile?: string; 
    error?: string;
  }[];
}

export const importEscortsToSupabase = async (): Promise<ImportResult> => {
  try {
    console.log('Starting import of escorts to Supabase...');
    // Get escorts as an array, not as a Promise
    const mockEscorts = await getEscorts();
    let importCount = 0;
    const results: { status: string; profile?: string; error?: string }[] = [];

    // Check if escorts already exist in Supabase
    const { data: existingProfiles, error: checkError } = await supabase
      .from('escort_profiles')
      .select('id, display_name');

    if (checkError) {
      console.error('Error checking existing profiles:', checkError);
      return { success: false, error: checkError.message, count: 0 };
    }

    // Create a set of existing display names for quick lookup
    const existingDisplayNames = new Set((existingProfiles || []).map(p => p.display_name.toLowerCase()));
    
    console.log(`${existingDisplayNames.size} existing profiles found in Supabase`);
    
    // If there are already profiles in Supabase, we can skip importing mock data
    if (existingDisplayNames.size > 0) {
      console.log('Profiles already exist in Supabase, skipping mock data import');
      return { 
        success: true, 
        count: 0,
        results: [{ status: 'skipped', profile: 'All profiles', error: 'Profiles already exist in Supabase' }]
      };
    }

    // Process each escort
    for (const escort of mockEscorts) {
      try {
        // Skip if this display name already exists
        if (existingDisplayNames.has(escort.displayName.toLowerCase())) {
          console.log(`Profile "${escort.displayName}" already exists, skipped`);
          results.push({ status: 'existant', profile: escort.displayName });
          continue;
        }

        // 1. Insert escort profile
        const escortData = {
          id: escort.id,
          user_id: escort.id,
          display_name: escort.displayName,
          age: escort.age,
          bio_short: escort.bio?.short || null,
          bio_full: escort.bio?.full || null,
          city: escort.location?.city || null,
          country: escort.location?.country || null,
          latitude: escort.location?.latitude || null,
          longitude: escort.location?.longitude || null,
          gender: escort.attributes?.gender || null,
          height: escort.attributes?.height || null,
          weight: escort.attributes?.weight || null,
          body_type: escort.attributes?.bodyType || null,
          hair_color: escort.attributes?.hairColor || null,
          eye_color: escort.attributes?.eyeColor || null,
          ethnicity: escort.attributes?.ethnicity || null,
          nationality: escort.attributes?.nationality || null,
          measurements: escort.attributes?.measurements || null,
          cup_size: escort.attributes?.cupSize || null,
          intimate_area: escort.attributes?.intimateArea || null,
          has_tattoos: typeof escort.attributes?.tattoos === 'boolean' ? escort.attributes.tattoos : null,
          has_piercings: typeof escort.attributes?.piercings === 'boolean' ? escort.attributes.piercings : null,
          smoking: escort.attributes?.smoking || null,
          drinking: escort.attributes?.drinking || null,
          sexual_orientation: escort.attributes?.sexualOrientation || null,
          is_verified: escort.isVerified || false,
          is_online: escort.isOnline || false,
          is_touring: escort.isTouring || false,
          ai_chat_enabled: escort.ai_chat_enabled || false,
          ai_chat_response_mode: escort.ai_chat_response_mode || 'always',
          ai_chat_schedule: escort.ai_chat_schedule || null
        };

        // Insert the escort profile
        const { data: insertedEscort, error: insertError } = await supabase
          .from('escort_profiles')
          .insert(escortData)
          .select('id')
          .single();

        if (insertError) {
          console.error(`Error inserting profile ${escort.displayName}:`, insertError);
          results.push({ status: 'échec', profile: escort.displayName, error: insertError.message });
          continue;
        }

        if (!insertedEscort) {
          console.error(`Unable to insert profile ${escort.displayName}, no ID returned`);
          results.push({ status: 'échec', profile: escort.displayName, error: 'No ID returned' });
          continue;
        }

        const escortId = insertedEscort.id;
        console.log(`Profile ${escort.displayName} inserted with ID: ${escortId}`);

        // 2. Insert escort services
        if (escort.services && escort.services.length > 0) {
          const serviceData = escort.services.map(service => ({
            escort_id: escortId,
            name: service.name,
            description: service.description || null,
            visibility: service.visibility || 'public'
          }));

          const { error: servicesError } = await supabase
            .from('escort_services')
            .insert(serviceData);

          if (servicesError) {
            console.error(`Error inserting services for ${escort.displayName}:`, servicesError);
          } else {
            console.log(`${serviceData.length} services inserted for ${escort.displayName}`);
          }
        }

        // 3. Insert escort rates
        if (escort.rates && escort.rates.length > 0) {
          const rateData = escort.rates.map(rate => ({
            escort_id: escortId,
            description: rate.description,
            price: rate.price,
            currency: rate.currency || 'EUR',
            duration: rate.duration || null,
            visibility: rate.visibility || 'public',
            category: rate.category || 'incall'
          }));

          const { error: ratesError } = await supabase
            .from('escort_rates')
            .insert(rateData);

          if (ratesError) {
            console.error(`Error inserting rates for ${escort.displayName}:`, ratesError);
          } else {
            console.log(`${rateData.length} rates inserted for ${escort.displayName}`);
          }
        }

        importCount++;
        results.push({ status: 'succès', profile: escort.displayName });
      } catch (profileError) {
        console.error(`Error processing profile ${escort.displayName}:`, profileError);
        results.push({ 
          status: 'échec', 
          profile: escort.displayName, 
          error: profileError instanceof Error ? profileError.message : 'Unknown error'
        });
      }
    }

    console.log(`Import completed successfully. ${importCount} profiles imported.`);
    return { success: true, count: importCount, results };
  } catch (error) {
    console.error('Global error during import:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error during import', 
      count: 0 
    };
  }
};
